/**
 * Resolves backend API endpoints at runtime — HOST-AWARE.
 *
 * Production hosts the app on Lovable/Cloudflare where `/api/*` does not
 * exist (requests fall through to the SPA and return index.html with 200).
 * Vercel serves `/api/*` but is the ACCEPTANCE environment. The Supabase
 * Edge Functions under `/functions/v1/*` are the production backend.
 *
 * See mem/constraints/backend-host-routing.md — v1.8.5 shipped hardcoded
 * `/api/...` calls and broke every server-backed action in production
 * (push, device-auth, invites, recipe parse, vapid key). This helper was
 * later flattened back to unconditional `/api/` during the Vercel
 * migration, silently reintroducing the same outage for all apiFetch
 * callers on clando.app. Do not remove the host detection again.
 */
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? "";
const SUPABASE_ANON_KEY =
  (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ?? "";

/**
 * Pure host → API-base decision, exported for unit tests.
 *
 * - Vercel hosts (`*.vercel.app`) serve the Node routes at `/api/*`.
 * - Local dev (Vite middleware) serves `/api/*` too.
 * - Everything else (clando.app on Lovable/Cloudflare, custom domains)
 *   routes to the Supabase Edge Functions.
 */
export function resolveApiBase(hostname: string, supabaseUrl: string = SUPABASE_URL): string {
  const isVercel = hostname.endsWith(".vercel.app");
  const isLocal =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("192.168.") ||
    hostname.endsWith(".local");
  if (isVercel || isLocal) return "/api";
  return `${supabaseUrl.replace(/\/$/, "")}/functions/v1`;
}

/**
 * Resolve a backend endpoint URL. Accepts either a bare name ("send-push"),
 * a path-prefixed form ("/send-push") or the legacy "/api/send-push".
 * Query strings are preserved.
 */
export function apiUrl(path: string): string {
  const cleaned = path.replace(/^\/+/, "").replace(/^api\//, "");
  const hostname = typeof window !== "undefined" ? window.location.hostname : "localhost";
  return `${resolveApiBase(hostname)}/${cleaned}`;
}

/**
 * Drop-in fetch wrapper. Resolves the URL via apiUrl() and injects the current
 * Supabase access token when available. When the resolved URL points at the
 * Supabase Edge gateway, the publishable key rides along as `apikey` (the
 * gateway requires it; Vercel routes ignore it). Caller-supplied headers
 * always win.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = apiUrl(path);
  const callerHeaders = new Headers(init.headers ?? {});
  if (!callerHeaders.has("authorization")) {
    let token: string | undefined;
    try {
      const { data } = await supabase.auth.getSession();
      token = data.session?.access_token ?? SUPABASE_ANON_KEY;
    } catch {
      token = SUPABASE_ANON_KEY || undefined;
    }
    if (token) callerHeaders.set("Authorization", `Bearer ${token}`);
  }
  if (url.includes("/functions/v1/") && !callerHeaders.has("apikey") && SUPABASE_ANON_KEY) {
    callerHeaders.set("apikey", SUPABASE_ANON_KEY);
  }
  return fetch(url, { ...init, headers: callerHeaders });
}
